import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Printer, ArrowUp, ChevronRight, AlertTriangle } from "lucide-react";

const EFFECTIVE_DATE = "June 30, 2026";

// ----------------------------------------------------------------------
// Terms sections. To edit copy, update this array only.
// ----------------------------------------------------------------------
const SECTIONS = [
  {
    n: 1, title: "Eligibility",
    body: [
      { p: "To use our website or purchase products or services from Melo Hopeful Care, you must:" },
      { ul: [
        "Be at least 18 years old or the age of majority in your jurisdiction.",
        "Have the legal capacity to enter into contracts.",
        "Provide accurate and truthful information.",
        "Use the website only for lawful purposes.",
      ]},
      { p: "If you are purchasing on behalf of another individual or organization, you represent that you have the authority to bind that individual or organization to these Terms." },
    ],
  },
  {
    n: 2, title: "Company Services",
    body: [
      { p: "Melo Hopeful Care may provide, including but not limited to:" },
      { ul: [
        "Home care services", "Personal care assistance", "Companion care", "Respite care",
        "Non-medical transportation coordination", "Wellness services", "Caregiver consultations",
        "Digital educational resources", "Membership services", "Subscription services",
        "Physical products", "Digital products", "Medical-related accessories where legally permitted",
        "Gift cards", "Merchandise", "Online scheduling", "Consultation services",
      ]},
      { p: "Not every service is available in every location." },
      { p: "Availability depends on licensing, staffing, regulations, and operational capacity." },
    ],
  },
  {
    n: 3, title: "No Medical Emergency Services", highlight: true,
    body: [
      { p: "Melo Hopeful Care does not provide emergency medical services." },
      { p: "If you are experiencing:" },
      { ul: [
        "Chest pain", "Difficulty breathing", "Severe injury", "Stroke symptoms",
        "Loss of consciousness", "Any life-threatening emergency",
      ]},
      { p: "Call your local emergency services immediately." },
      { p: "Our website is not monitored for emergency communications." },
    ],
  },
  {
    n: 4, title: "Website Use",
    body: [
      { p: "You agree to use the website responsibly." },
      { p: "You may not:" },
      { ul: [
        "Violate any law.", "Hack or interfere with the website.", "Upload malware.",
        "Attempt unauthorized access.", "Copy website content without permission.",
        "Use automated bots without authorization.", "Impersonate another individual.",
        "Submit false information.", "Engage in fraudulent activity.",
        "Reverse engineer website software.",
      ]},
      { p: "Violation may result in account suspension, legal action, or permanent termination." },
    ],
  },
  {
    n: 5, title: "User Accounts",
    body: [
      { p: "Certain services require account registration." },
      { p: "You agree to:" },
      { ul: [
        "Keep login credentials confidential.",
        "Maintain accurate information.",
        "Notify us immediately of unauthorized access.",
        "Accept responsibility for all activity under your account.",
      ]},
      { p: "We reserve the right to suspend or terminate accounts for suspected fraud, abuse, or violations of these Terms." },
    ],
  },
  {
    n: 6, title: "Products and Services",
    body: [
      { p: "Products and services displayed on our website are subject to availability." },
      { p: "Descriptions, images, colors, pricing, packaging, specifications, and availability may change without notice." },
      { p: "We reserve the right to discontinue products or services at any time." },
      { p: "Product images are illustrative and may vary slightly from actual products." },
    ],
  },
  {
    n: 7, title: "Pricing",
    body: [
      { p: "All prices are listed in U.S. Dollars unless otherwise stated." },
      { p: "Prices may change without notice." },
      { p: "Taxes, shipping charges, convenience fees, service fees, and other applicable charges are calculated during checkout where applicable." },
      { p: "Pricing errors may occasionally occur." },
      { p: "If an obvious pricing error is identified, Melo Hopeful Care reserves the right to cancel the affected order." },
    ],
  },
  {
    n: 8, title: "Orders",
    body: [
      { p: "Submitting an order does not guarantee acceptance." },
      { p: "We reserve the right to:" },
      { ul: [
        "Reject orders.", "Cancel orders.", "Limit quantities.", "Refuse service.",
        "Request identity verification.", "Require additional payment verification.",
      ]},
      { p: "You will receive an order confirmation acknowledging receipt, not acceptance." },
      { p: "Acceptance occurs when your order has been processed or your services begin." },
    ],
  },
  {
    n: 9, title: "Payments",
    body: [
      { p: "We accept approved payment methods displayed during checkout." },
      { p: "By submitting payment information, you certify that:" },
      { ul: [
        "You are authorized to use the payment method.",
        "Billing information is accurate.",
        "Funds are available.",
      ]},
      { p: "Failed payments may result in delayed or canceled orders." },
      { p: "Recurring services authorize recurring billing until canceled according to applicable terms." },
    ],
  },
  {
    n: 10, title: "Subscription Services",
    body: [
      { p: "Certain products or services may be offered through recurring subscriptions." },
      { p: "Subscriptions automatically renew unless canceled before the renewal date." },
      { p: "Subscription pricing may change with reasonable notice." },
      { p: "You may cancel according to your subscription agreement." },
      { p: "Refunds for subscription services are governed by our Refund Policy." },
    ],
  },
  {
    n: 11, title: "Shipping",
    body: [
      { p: "Shipping estimates are approximate." },
      { p: "Delivery times are not guaranteed." },
      { p: "Shipping delays may result from:" },
      { ul: ["Weather", "Carrier delays", "Customs", "Inventory shortages", "Supply chain disruptions", "Natural disasters", "Government actions"] },
      { p: "Risk of loss transfers upon delivery to the carrier where permitted by law." },
    ],
  },
  {
    n: 12, title: "Returns and Refunds",
    body: [
      { p: "Returns are subject to our Refund Policy." },
      { p: "Certain items cannot be returned, including:" },
      { ul: [
        "Personalized products", "Digital downloads", "Opened hygiene items",
        "Customized products", "Services already rendered",
      ]},
      { p: "Refunds may be denied if products show misuse, damage, or unauthorized modification." },
      { p: "Approved refunds are issued using the original payment method whenever possible." },
    ],
  },
  {
    n: 13, title: "Appointment Scheduling",
    body: [
      { p: "Appointments may be scheduled online." },
      { p: "Appointment requests are subject to staff availability." },
      { p: "Missed appointments, late arrivals, or cancellations may incur fees." },
      { p: "Repeated no-shows may result in suspension of scheduling privileges." },
    ],
  },
  {
    n: 14, title: "Home Care Services",
    body: [
      { p: "Home care services are provided according to applicable laws and regulations." },
      { p: "Service availability depends upon:" },
      { ul: ["Care assessments", "Staffing", "Geographic location", "Scheduling", "Licensing requirements"] },
      { p: "Care plans may be modified as circumstances change." },
    ],
  },
  {
    n: 15, title: "Client Responsibilities",
    body: [
      { p: "Clients agree to:" },
      { ul: [
        "Provide accurate information.", "Maintain a safe environment.", "Cooperate with caregivers.",
        "Report changes in health conditions.", "Provide necessary access to the residence.",
        "Treat staff respectfully.",
      ]},
      { p: "Unsafe conditions may result in service suspension." },
    ],
  },
  {
    n: 16, title: "Intellectual Property",
    body: [
      { p: "All website content including:" },
      { ul: [
        "Logos", "Graphics", "Videos", "Software", "Documents", "Text",
        "Articles", "Images", "Designs", "Branding", "Trademarks",
      ]},
      { p: "remain the exclusive property of Melo Hopeful Care or its licensors." },
      { p: "No ownership rights are transferred through website use." },
      { p: "Unauthorized use is prohibited." },
    ],
  },
  {
    n: 17, title: "Copyright",
    body: [
      { p: "All copyrighted material is protected by United States copyright law and applicable international treaties." },
      { p: "You may not reproduce, distribute, modify, publish, or commercially exploit website materials without written permission." },
    ],
  },
  {
    n: 18, title: "Trademark Rights",
    body: [
      { p: "\"Melo Hopeful Care,\" logos, slogans, service marks, trade dress, and related branding belong to the Company unless otherwise indicated." },
      { p: "Unauthorized use is prohibited." },
    ],
  },
  {
    n: 19, title: "User Content",
    body: [
      { p: "If you submit:" },
      { ul: ["Reviews", "Testimonials", "Comments", "Suggestions", "Photos", "Videos", "Feedback"] },
      { p: "you grant Melo Hopeful Care a non-exclusive, royalty-free, perpetual, worldwide license to use, reproduce, publish, modify, distribute, and display such content in connection with our business, subject to applicable privacy laws." },
      { p: "You represent that you own or have permission to submit such content." },
    ],
  },
  {
    n: 20, title: "Privacy",
    body: [
      { p: "Use of our services is also governed by our Privacy Policy." },
      { p: "By using our website, you consent to the collection and use of information as described in that policy." },
    ],
  },
  {
    n: 21, title: "HIPAA and Health Information",
    body: [
      { p: "Where applicable, Melo Hopeful Care handles protected health information in accordance with applicable federal and state laws, including the Health Insurance Portability and Accountability Act (HIPAA)." },
      { p: "Not all information submitted through the website constitutes protected health information." },
      { p: "Users should avoid transmitting unnecessary sensitive medical information through general website forms unless specifically requested." },
    ],
  },
  {
    n: 22, title: "Communications",
    body: [
      { p: "By providing contact information, you consent to receive communications regarding:" },
      { ul: ["Orders", "Appointments", "Billing", "Customer support", "Service updates", "Security notices"] },
      { p: "Where permitted by law, you may also receive marketing communications, from which you may opt out at any time." },
    ],
  },
  {
    n: 23, title: "Electronic Signatures",
    body: [
      { p: "Electronic signatures, clicks, acknowledgments, and digital acceptances carry the same legal effect as handwritten signatures where permitted by applicable law." },
    ],
  },
  {
    n: 24, title: "Third-Party Services",
    body: [
      { p: "Our website may integrate with third-party services, including payment processors, scheduling tools, mapping services, analytics providers, or shipping carriers." },
      { p: "We are not responsible for the content, policies, availability, or practices of third-party providers." },
    ],
  },
  {
    n: 25, title: "Links to Other Websites",
    body: [
      { p: "Our website may contain links to external websites." },
      { p: "We do not control or endorse those websites and are not responsible for their content or availability." },
      { p: "Accessing third-party websites is at your own risk." },
    ],
  },
  {
    n: 26, title: "Disclaimer of Warranties",
    body: [
      { p: "Except as expressly provided by law, our website, products, and services are provided on an \"as is\" and \"as available\" basis." },
      { p: "To the fullest extent permitted by law, Melo Hopeful Care disclaims all warranties, whether express, implied, statutory, or otherwise, including implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement." },
      { p: "We do not warrant that the website will be uninterrupted, error-free, secure, or free of harmful components." },
    ],
  },
  {
    n: 27, title: "Limitation of Liability",
    body: [
      { p: "To the fullest extent permitted by applicable law, Melo Hopeful Care, its affiliates, officers, directors, employees, contractors, licensors, and agents shall not be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages, including loss of profits, data, goodwill, business opportunities, or other intangible losses arising from or related to the use of our website, products, or services." },
      { p: "Where liability cannot be excluded by law, our total liability shall not exceed the amount paid by you for the applicable product or service giving rise to the claim." },
      { p: "Some jurisdictions do not allow certain limitations of liability, so portions of this section may not apply to you." },
    ],
  },
  {
    n: 28, title: "Indemnification",
    body: [
      { p: "You agree to defend, indemnify, and hold harmless Melo Hopeful Care and its affiliates, officers, employees, contractors, licensors, successors, and assigns from and against any claims, liabilities, damages, losses, costs, or expenses, including reasonable attorneys' fees, arising from your violation of these Terms, misuse of the website, infringement of any rights of another person, or violation of applicable law." },
    ],
  },
  {
    n: 29, title: "Force Majeure",
    body: [
      { p: "Melo Hopeful Care shall not be liable for any delay or failure to perform obligations due to events beyond its reasonable control, including natural disasters, severe weather, pandemics, epidemics, labor disputes, power failures, cyberattacks, internet outages, governmental actions, acts of terrorism, war, civil unrest, transportation disruptions, or shortages of materials or personnel." },
    ],
  },
  {
    n: 30, title: "Compliance with Laws",
    body: [
      { p: "You agree to comply with all applicable local, state, federal, and international laws and regulations in connection with your use of our website and services." },
    ],
  },
  {
    n: 31, title: "Accessibility",
    body: [
      { p: "Melo Hopeful Care is committed to making its website accessible to individuals with disabilities and continually works to improve accessibility. If you encounter accessibility barriers, please contact us so we can assist." },
    ],
  },
  {
    n: 32, title: "Dispute Resolution",
    body: [
      { p: "Before initiating formal legal proceedings, the parties agree to attempt to resolve disputes through good-faith negotiations." },
      { p: "If a dispute cannot be resolved informally, the parties may agree to mediation or another alternative dispute resolution process where permitted by law." },
      { p: "Nothing in this section limits any rights that cannot legally be waived." },
    ],
  },
  {
    n: 33, title: "Governing Law",
    body: [
      { p: "These Terms and Conditions shall be governed by and interpreted in accordance with the laws of the state in which Melo Hopeful Care maintains its principal place of business, without regard to conflict-of-law principles, except where superseded by applicable federal law." },
    ],
  },
  {
    n: 34, title: "Severability",
    body: [
      { p: "If any provision of these Terms is determined by a court of competent jurisdiction to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect." },
    ],
  },
  {
    n: 35, title: "Waiver",
    body: [
      { p: "Failure by Melo Hopeful Care to enforce any provision of these Terms shall not constitute a waiver of that provision or any other rights." },
    ],
  },
  {
    n: 36, title: "Assignment",
    body: [
      { p: "You may not assign or transfer your rights or obligations under these Terms without our prior written consent." },
      { p: "Melo Hopeful Care may assign its rights or obligations without restriction in connection with a merger, acquisition, corporate restructuring, financing, or sale of assets." },
    ],
  },
  {
    n: 37, title: "Entire Agreement",
    body: [
      { p: "These Terms, together with our Privacy Policy, Refund Policy, and any additional policies referenced herein, constitute the entire agreement between you and Melo Hopeful Care regarding your use of the website and supersede all prior communications and understandings relating to the subject matter." },
    ],
  },
  {
    n: 38, title: "Changes to These Terms",
    body: [
      { p: "We reserve the right to modify, revise, or update these Terms at any time." },
      { p: "Changes become effective upon posting the updated version on our website unless otherwise required by law." },
      { p: "Your continued use of the website after changes are posted constitutes acceptance of the revised Terms." },
    ],
  },
  {
    n: 39, title: "Contact Information",
    body: [
      { p: "Questions regarding these Terms and Conditions may be directed to Melo Hopeful Care using the contact information published on our official website." },
    ],
  },
  {
    n: 40, title: "Acknowledgment",
    body: [
      { p: "By accessing, using, registering with, purchasing from, or otherwise interacting with Melo Hopeful Care's website, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions." },
      { p: "If you do not agree with any part of these Terms, you must discontinue use of the website immediately." },
      { p: "Thank you for choosing Melo Hopeful Care. We appreciate the opportunity to serve you and are committed to providing quality products, compassionate services, secure online experiences, and respectful customer support while maintaining compliance with applicable laws and regulations." },
    ],
  },
];

const slug = (n, title) => `s${n}-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

function SectionBody({ blocks }) {
  return (
    <div className="space-y-4">
      {blocks.map((b, i) =>
        b.ul ? (
          <ul key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 pl-0">
            {b.ul.map((li) => (
              <li key={li} className="flex items-start gap-2 text-slate-700 leading-relaxed">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" />
                <span>{li}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p key={i} className="text-slate-700 leading-relaxed">{b.p}</p>
        )
      )}
    </div>
  );
}

export default function Terms() {
  const [active, setActive] = useState(SECTIONS[0].n);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const n = Number(e.target.getAttribute("data-section-n"));
            if (n) setActive(n);
          }
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    document.querySelectorAll("[data-section-n]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const printPage = () => window.print();

  return (
    <div data-testid="terms-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#072A47] text-white">
        <div className="grain opacity-20" />
        <div className="container-x pt-16 pb-14 md:pt-20 md:pb-16">
          <div className="flex items-center gap-3 text-teal-200/90">
            <FileText className="w-4 h-4" />
            <span className="text-xs uppercase tracking-[0.22em] font-semibold">Legal</span>
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold tracking-tight leading-[1.05]">
            Terms &amp; Conditions
          </h1>
          <p className="mt-4 text-white/80 max-w-2xl leading-relaxed">
            The rules of the road for using our website and services. Please read carefully before purchasing, booking, or applying.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
            <span className="pill bg-white/10 border border-white/20 text-white/90" data-testid="terms-effective-date">
              Effective {EFFECTIVE_DATE}
            </span>
            <button onClick={printPage} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm font-medium transition print:hidden" data-testid="terms-print-btn">
              <Printer className="w-4 h-4" /> Print / Save as PDF
            </button>
          </div>
        </div>
      </section>

      {/* INTRO + LAYOUT */}
      <section className="section">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          {/* SIDEBAR TOC */}
          <aside className="lg:col-span-4 xl:col-span-3 print:hidden" data-testid="terms-toc">
            <div className="lg:sticky lg:top-28 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
              <div className="text-xs uppercase tracking-[0.22em] text-teal-700 font-semibold mb-4">On this page</div>
              <nav className="space-y-0.5 border-l border-slate-200">
                {SECTIONS.map((s) => (
                  <a
                    key={s.n}
                    href={`#${slug(s.n, s.title)}`}
                    data-testid={`toc-link-${s.n}`}
                    className={`group flex items-start gap-2 pl-4 pr-2 py-1.5 -ml-px border-l-2 text-[13px] leading-snug transition ${
                      active === s.n
                        ? "border-teal-500 text-brand-navy font-semibold bg-teal-50/40"
                        : "border-transparent text-slate-600 hover:text-brand-navy hover:border-slate-300"
                    }`}
                  >
                    <span className="text-slate-400 tabular-nums shrink-0 w-6 text-right">{s.n}.</span>
                    <span className="flex-1">{s.title}</span>
                    <ChevronRight className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${active === s.n ? "text-teal-600" : "text-transparent group-hover:text-slate-400"}`} />
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* MAIN */}
          <article className="lg:col-span-8 xl:col-span-9 max-w-3xl" data-testid="terms-body">
            <div className="soft-card !p-8 md:!p-10">
              <p className="text-slate-700 leading-relaxed">
                Welcome to Melo Hopeful Care. These Terms and Conditions ("Terms") govern your access to and use of the Melo Hopeful Care website, online store, products, services, digital platforms, customer portals, mobile applications, and any related services provided by Melo Hopeful Care ("Company," "we," "our," or "us").
              </p>
              <p className="mt-4 text-slate-700 leading-relaxed">
                By accessing, browsing, purchasing from, registering with, or otherwise using our website or services, you acknowledge that you have read, understood, and agree to be legally bound by these Terms and Conditions. If you do not agree with these Terms, you should not use our website or purchase any products or services.
              </p>
              <p className="mt-4 text-slate-700 leading-relaxed">
                These Terms constitute a legally binding agreement between you and Melo Hopeful Care.
              </p>
            </div>

            <div className="mt-10 space-y-10">
              {SECTIONS.map((s) => (
                <section
                  key={s.n}
                  id={slug(s.n, s.title)}
                  data-section-n={s.n}
                  data-testid={`terms-section-${s.n}`}
                  className="scroll-mt-28"
                >
                  <div className="flex items-baseline gap-4">
                    <span className="text-xs uppercase tracking-[0.22em] font-semibold text-teal-700 tabular-nums">
                      Section {String(s.n).padStart(2, "0")}
                    </span>
                  </div>
                  <h2 className="mt-2 text-2xl sm:text-3xl font-heading font-semibold text-slate-900 tracking-tight">
                    {s.title}
                  </h2>
                  {s.highlight && (
                    <div className="mt-4 rounded-xl border-l-4 border-red-500 bg-red-50/70 px-5 py-4 flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                      <div className="text-sm text-red-800">
                        This website and Melo Hopeful Care do <strong>not</strong> provide emergency medical services. In an emergency, always call your local emergency number (911 in the U.S.) immediately.
                      </div>
                    </div>
                  )}
                  <div className="mt-4">
                    <SectionBody blocks={s.body} />
                  </div>
                </section>
              ))}
            </div>

            <div className="mt-16 pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <div className="text-sm text-slate-500">Last updated: {EFFECTIVE_DATE}</div>
              <div className="flex items-center gap-3 print:hidden">
                <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="btn-ghost" data-testid="terms-back-to-top">
                  <ArrowUp className="w-4 h-4" /> Back to top
                </button>
                <Link to="/contact" className="btn-outline" data-testid="terms-contact-link">
                  Questions? Contact us
                </Link>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
